import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { shortDate } from "../lib/weekLine";
import {
  addExerciseAt, emptyTree, exercisesOf, findRow, moveMany, runOf, sectionsOf, type LibraryPick, type Landing, type WeekTree,
} from "../lib/weekEdit";
import type { CreateEntry } from "../lib/weekEdit";
import { useWeekEditor } from "./WeekEditor";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * WEEK EDITOR STAGE 1 — DRAG A ROW TO ANOTHER DAY: THE ACCELERATOR for the row's `⋯ → Move to…` (DECIDED: drag is only ever an
 * accelerator; the menu is the keyboard's path). A SUPERSET MOVES AS ONE (William
 * 10-19: "if I drag it, that superset gets dragged"); `⋯ → Move just this exercise…` is the way to take one member.
 *
 * THE WHOLE ROW IS THE HANDLE (Ioannis 10-19). Pointer events, not HTML5 drag (codex `web-css`: an HTML drag is proven only by a drag the browser itself started), and not
 * the week's dnd-kit area either — that one carries WORKOUTS, and a second sortable inside it would bind to the nearest
 * context and break the workout drag. A press that moves under 5 px is no drag. While carried: a small card under the pointer
 * (in a portal — the column is a size container, which would trap a fixed card), a line where it would land, Escape cancels.
 * Where it lands is asked of the TREE (`afterRowId`), never of the drawn order alone.
 *
 * STAGE 2 (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md`): ONE CARRY for three things — a row (its superset), the coach's
 * SELECTION (rows on several days), and an exercise from the LIBRARY panel. Same target finder, same line, same Escape; while
 * anything is carried `html.is-carrying` lets a floating library panel step aside so every day is a target.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export type Target =
  | { kind: "workout"; workoutId: string; afterRowId?: string | null; sectionId?: string; line: DOMRect }
  /* `index` + `line`: a WORKOUT's place among that day's workouts (Ioannis 10-19: "so the order … [is] as intended"). */
  | { kind: "day"; dayKey: string; box: DOMRect; index?: number; line?: DOMRect };

/** What is carried: rows (`picks` by workout, `order` = the workouts in week order) or a library exercise. */
export type Carry =
  | { kind: "rows"; picks: ReadonlyMap<string, readonly string[]>; order: readonly string[]; words: string }
  | { kind: "library"; pick: LibraryPick; words: string }
  /* A library WORKOUT (the panel's Workouts tab, Ioannis 10-19 "we need both tabs"): it lands on a DAY — assigned there, the
     page's own `assign_coach_workout` (copy-on-assign), never written into another workout. */
  | { kind: "workout"; workout: LibraryWorkout; words: string };

export type LibraryWorkout = { id: string; rootId: string; name: string };

type Editor = NonNullable<ReturnType<typeof useWeekEditor>>;

function movingIds(c: Carry): string[] {
  return c.kind === "rows" ? [...c.picks.values()].flat() : [];
}

export function targetAt(ed: Editor, x: number, y: number, moving: readonly string[], dayOnly = false): Target | null {
  const el = document.elementFromPoint(x, y);
  const box = dayOnly ? null : el?.closest<HTMLElement>("[data-drop-workout]");
  if (box !== null && box !== undefined) {
    const id = box.dataset.dropWorkout ?? "";
    if (ed.locked(id) || ed.treeOf(id) === undefined) return null;
    const rows = [...box.querySelectorAll<HTMLElement>("[data-row-id]")].filter((r) => !moving.includes(r.dataset.rowId ?? ""));
    const rect = box.getBoundingClientRect();
    let after: HTMLElement | null = null;
    for (const r of rows) { const b = r.getBoundingClientRect(); if (b.top + b.height / 2 < y) after = r; }
    const t = ed.treeOf(id)!;
    if (after === null) {
      const first = sectionsOf(t).flatMap((s) => exercisesOf(s).filter((e) => !moving.includes(e.id)).map(() => s.id))[0];
      const top = rows[0]?.getBoundingClientRect().top ?? rect.bottom - 4;
      const line = new DOMRect(rect.left, top - 1, rect.width, 2);
      return first === undefined ? { kind: "workout", workoutId: id, line } : { kind: "workout", workoutId: id, afterRowId: null, sectionId: first, line };
    }
    const b = after.getBoundingClientRect();
    return { kind: "workout", workoutId: id, afterRowId: after.dataset.rowId ?? null, line: new DOMRect(rect.left, b.bottom - 1, rect.width, 2) };
  }
  const day = el?.closest<HTMLElement>("[data-drop-day]");
  if (day !== null && day !== undefined) {
    const key = day.dataset.dropDay ?? "";
    const d = ed.days.find((x) => x.dayKey === key);
    if (d === undefined) return null;
    if (d.workouts.length > 0 && d.workouts.every((w) => w.trained)) return null;
    const box = day.getBoundingClientRect();
    if (!dayOnly) return { kind: "day", dayKey: key, box };
    /* A workout lands BETWEEN the day's workouts: before the first whose middle is below the pointer. */
    const items = [...day.querySelectorAll<HTMLElement>(".week-col-items > li")];
    let index = 0;
    for (const it of items) { const b = it.getBoundingClientRect(); if (b.top + b.height / 2 < y) index += 1; }
    const edge = items.length === 0 ? null
      : index < items.length ? items[index]!.getBoundingClientRect().top - 1 : items[items.length - 1]!.getBoundingClientRect().bottom - 1;
    return { kind: "day", dayKey: key, box, index, line: edge === null ? undefined : new DOMRect(box.left, edge, box.width, 2) };
  }
  return null;
}

/** Where a carry lands, as ONE change: the target workout (or a new copy on an empty day) and every source tree it leaves. */
export function landCarry(ed: Editor, carry: Carry, t: Target): void {
  if (carry.kind === "workout") {
    if (t.kind === "day") ed.assign(carry.workout, t.dayKey, t.index ?? 0);
    return;
  }
  let to: WeekTree;
  let landing: Landing;
  let dayKey: string | undefined;
  const creates = new Map<string, CreateEntry>();
  const firstSource = carry.kind === "rows" ? ed.treeOf(carry.order[0] ?? "") : undefined;
  const newName = carry.kind === "rows" ? firstSource?.name ?? "Workout" : "Workout";
  if (t.kind === "workout") {
    to = ed.treeOf(t.workoutId)!;
    landing = t.afterRowId === undefined ? { workoutId: t.workoutId } : { workoutId: t.workoutId, afterRowId: t.afterRowId, sectionId: t.sectionId };
    dayKey = ed.dayOf(t.workoutId);
  } else {
    dayKey = t.dayKey;
    const d = ed.days.find((x) => x.dayKey === t.dayKey)!;
    const open = d.workouts.filter((w) => !w.trained);
    const last = open[open.length - 1];
    if (last !== undefined) {
      to = ed.treeOf(last.workoutId) ?? emptyTree(last.workoutId, last.name);
      landing = { workoutId: last.workoutId };
    } else {
      const id = ed.newId();
      to = emptyTree(id, newName);
      landing = { workoutId: id };
      creates.set(id, { assignment_id: ed.newId(), client_id: ed.clientId, scheduled_date: t.dayKey, name: newName });
    }
  }
  const where = dayKey === undefined ? to.name : shortDate(dayKey);
  if (carry.kind === "library") {
    const { tree, rowId } = addExerciseAt(to, carry.pick, landing, ed.newId);
    void ed.change(`${carry.pick.name} added to ${where}`, new Map([[to.id, tree]]), creates).then((ok) => { if (ok) ed.setEditing(rowId); });
    return;
  }
  const sources = carry.order.map((id) => ed.treeOf(id)).filter((x): x is WeekTree => x !== undefined);
  const after = moveMany(sources, carry.picks, to, landing, false, ed.newId) as Map<string, WeekTree | null>;
  const within = carry.order.length === 1 && carry.order[0] === to.id;
  ed.clearSelection();
  void ed.change(`${carry.words} moved ${within ? "within the workout" : `to ${where}`}`, after, creates);
}

/** The carry's lifetime: a press that moves 5 px becomes a carry; the card, the landing line, Escape, the drop. */
export function useCarry(): { begin: (e: React.PointerEvent, make: () => Carry | null) => void; overlay: ReactNode; swallowClick: React.MutableRefObject<boolean> } {
  const ed = useWeekEditor();
  const [drag, setDrag] = useState<{ x: number; y: number; target: Target | null; carry: Carry } | null>(null);
  const live = useRef<typeof drag>(null);
  live.current = drag;
  const swallowClick = useRef(false);

  useEffect(() => {
    if (drag === null) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      undim();
      live.current = null;
      setDrag(null);
    };
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("keydown", esc); };
  }, [drag !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  function undim() {
    document.querySelectorAll("[data-row-id].is-carried").forEach((el) => { el.classList.remove("is-carried"); });
    document.documentElement.classList.remove("is-carrying");
  }

  function begin(e: React.PointerEvent, make: () => Carry | null) {
    if (e.button !== 0 || ed === null || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const x0 = e.clientX, y0 = e.clientY;
    let started = false;
    const move = (m: PointerEvent) => {
      if (!started) {
        if (Math.hypot(m.clientX - x0, m.clientY - y0) < 5) return;
        const carry = make();
        if (carry === null) { stop(); return; }
        started = true;
        window.getSelection()?.removeAllRanges();
        document.documentElement.classList.add("is-carrying");
        /* Everything carried dims — the superset's partner and every picked row, not only the row under the pointer. */
        for (const id of movingIds(carry)) document.querySelectorAll(`[data-row-id="${id}"]`).forEach((el) => { el.classList.add("is-carried"); });
        setDrag({ x: m.clientX, y: m.clientY, target: targetAt(ed, m.clientX, m.clientY, movingIds(carry), carry.kind === "workout"), carry });
        return;
      }
      m.preventDefault();
      const d = live.current;
      if (d !== null) setDrag({ ...d, x: m.clientX, y: m.clientY, target: targetAt(ed, m.clientX, m.clientY, movingIds(d.carry), d.carry.kind === "workout") });
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
    const up = () => {
      stop();
      if (!started) return;
      undim();
      swallowClick.current = true;
      const d = live.current;
      live.current = null;
      setDrag(null);
      if (d?.target != null && ed !== null) landCarry(ed, d.carry, d.target);
    };
    const cancel = () => {
      stop();
      undim();
      live.current = null;
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  }

  const overlay = drag === null ? null : createPortal(
    <>
      <span className="week-drag-card" style={{ left: drag.x + 12, top: drag.y + 8 }} aria-hidden="true">{drag.carry.words}</span>
      {drag.target?.kind === "workout" && (
        <span className="week-drag-line" style={{ left: drag.target.line.left, top: drag.target.line.top, width: drag.target.line.width }} aria-hidden="true" />
      )}
      {drag.target?.kind === "day" && drag.target.line !== undefined && (
        <span className="week-drag-line" style={{ left: drag.target.line.left, top: drag.target.line.top, width: drag.target.line.width }} aria-hidden="true" />
      )}
      {drag.target?.kind === "day" && drag.target.line === undefined && (
        <span className="week-drag-day" style={{ left: drag.target.box.left, top: drag.target.box.top, width: drag.target.box.width, height: drag.target.box.height }} aria-hidden="true" />
      )}
    </>,
    document.body,
  );
  return { begin, overlay, swallowClick };
}

/**
 * The whole ROW is the handle (Ioannis 10-19: "can't we just drag exercises?"): press anywhere on it and move — a press that
 * moves under 5 px stays a click (the name still opens, the line still edits). Not from inside the open field or the `⋯`.
 * The move and the release are heard on the WINDOW, never by capturing the pointer on the row: a capture would retarget the
 * plain click away from the name's button.
 * STAGE 2: a picked row carries the whole SELECTION; ⌘-click (Ctrl) picks or un-picks a row, ⇧-click picks the rows between.
 */
export function RowDrag({ workoutId, rowId, children }: { workoutId: string; rowId: string; children: ReactNode }) {
  const ed = useWeekEditor();
  const { begin, overlay, swallowClick } = useCarry();
  if (ed === null) return <>{children}</>;
  const src = ed.treeOf(workoutId);

  const name = (t: WeekTree | undefined, id: string) => {
    const at = t === undefined ? null : findRow(t, id);
    return at === null || t === undefined ? "" : exercisesOf(at.section)[at.index]?.coach_exercises?.name ?? "";
  };

  function onPointerDown(e: React.PointerEvent) {
    if (src === undefined || ed === null || ed.locked(workoutId)) return;
    const from = e.target as HTMLElement;
    if (from.closest("textarea, input, .week-row-menu, .week-line-edit") !== null) return;
    begin(e, () => {
      if (ed.selected.has(rowId)) {
        const { picks, order } = ed.selectionPicks();
        const n = [...picks.values()].reduce((a, r) => a + r.length, 0);
        return { kind: "rows", picks, order, words: n === 1 ? name(src, rowId) : `${String(n)} exercises` };
      }
      const rows = runOf(src, rowId);
      return { kind: "rows", picks: new Map([[workoutId, rows]]), order: [workoutId], words: rows.map((id) => name(src, id)).join(" + ") };
    });
  }

  const picked = ed.selected.has(rowId);
  return (
    <div className={picked ? "week-row-drag is-picked" : "week-row-drag"} title="Drag to another day" onPointerDown={onPointerDown}
         onMouseDown={(e) => { if (e.shiftKey || e.metaKey || e.ctrlKey) e.preventDefault(); }}
         onClickCapture={(e) => {
           if (swallowClick.current) { swallowClick.current = false; e.stopPropagation(); e.preventDefault(); return; }
           if (ed.locked(workoutId) || !(e.metaKey || e.ctrlKey || e.shiftKey)) return;
           if ((e.target as HTMLElement).closest("textarea, input, .week-row-menu, .week-line-edit") !== null) return;
           e.stopPropagation(); e.preventDefault();
           ed.pick(workoutId, rowId, e.shiftKey ? "range" : "toggle");
         }}
         onDragStart={(e) => { e.preventDefault(); }}>
      {children}
      {overlay}
    </div>
  );
}
